import * as React from 'react';
import type {
	IOption,
	ISelectOptionRenderState,
	SelectOptionValue,
	SelectSelectionMode,
} from '../types';
import { CaptionTypography, CheckMark } from '@components/atoms';
import { cn } from '@utils';

interface ISelectOptionButtonProps<
	TValue extends SelectOptionValue = SelectOptionValue,
	TMetadata = unknown
> {
	getOptionId: (option: IOption<TValue, TMetadata>) => string | undefined;
	highlighted: boolean;
	onOptionSelect: (option: IOption<TValue, TMetadata>) => void;
	option: IOption<TValue, TMetadata>;
	optionRef?: React.Ref<HTMLButtonElement>;
	renderOption?: (option: IOption<TValue, TMetadata>, state: ISelectOptionRenderState) => React.ReactNode;
	selected: boolean;
	selectionMode: SelectSelectionMode;
}

const OPTION_TAB_INDEX = -1;

export const SelectOptionButton = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	getOptionId,
	highlighted,
	onOptionSelect,
	option,
	optionRef,
	renderOption,
	selected,
	selectionMode,
}: ISelectOptionButtonProps<TValue, TMetadata>): React.ReactElement => {
	const disabled = Boolean(option.disabled);
	const optionContent = renderOption
		? renderOption(option, { disabled, highlighted, selected, selectionMode })
		: option.label;
	const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>): void => {
		event.preventDefault();
	};

	return (
		<button
			aria-disabled={disabled}
			aria-selected={selected}
			className={cn(
				'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-primary transition-colors enabled:hover:bg-bgPrimary focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
				selected && 'bg-highlightBorder',
				highlighted && 'ring-2 ring-inset ring-highlightSelected'
			)}
			data-testid="select-option"
			disabled={disabled}
			id={getOptionId(option)}
			onClick={() => onOptionSelect(option)}
			onMouseDown={handleMouseDown}
			ref={optionRef}
			role="option"
			tabIndex={OPTION_TAB_INDEX}
			type="button"
		>
			<span className="flex h-4 w-4 shrink-0 items-center justify-center">
				{selected && <CheckMark aria-hidden="true" className="h-4 w-4 text-primary" />}
			</span>
			<span className="min-w-0 flex-1 text-xs text-primary">
				{typeof optionContent === 'string' ? (
					<CaptionTypography className="text-primary">{optionContent}</CaptionTypography>
				) : (
					optionContent
				)}
			</span>
		</button>
	);
};
